import NextImage, { ImageLoaderProps } from 'next/image';
import get from 'ts-get';

import { useRutilusQuery } from '../../../api/graphql/useRutilusQuery';
import { Avatar, AvatarSize } from '../../../components/Avatar/Avatar';
import { CatchCard } from '../../../components/Cards/CatchCard/CatchCard';
import { Grid } from '../../../components/Grid/Grid';
import { Heading } from '../../../components/Heading/Heading';
import { MultiPhotoIcon } from '../../../components/Icons/MultiPhotoIcon';
import { Text } from '../../../components/Text/Text';
import { getNodesFromConnection } from '../../../helpers/graphql';
import { getImgProxyLoader } from '../../../helpers/imageLoaders/imgProxy';
import { getPostUrl } from '../../../helpers/urls';
import {
  GetUserCatchesExcludingCatch,
  GetUserCatchesExcludingCatchVariables,
} from '../../../interfaces/graphql';
import { getUserCatchesExcludingCatch } from '../../../queries/rutilus/Catches';

interface IProps {
  userId: string;
  excludeId: string;
  userNickname: string;
  customHeading?: string;
}

export const OtherCatchesFromThisUser = ({
  userId,
  excludeId,
  userNickname,
  customHeading,
}: IProps) => {
  const { data } = useRutilusQuery<
    GetUserCatchesExcludingCatch,
    GetUserCatchesExcludingCatchVariables
  >(getUserCatchesExcludingCatch, {
    variables: {
      externalId: userId,
      excludeId,
    },
  });

  const catchList = getNodesFromConnection(data?.entry.catches);

  if (!Array.isArray(catchList) || catchList.length === 0) {
    return <></>;
  }

  return (
    <div className="py-sm" data-testid="OtherCatchesFromThisUser">
      <Heading variant="md" className="pb-sm text-center break-words">
        {customHeading ?? `More catches from ${userNickname}`}
      </Heading>
      <Grid gapType="x">
        <div className="col-span-full grid grid-cols-2 gap-1 sm:grid-cols-4">
          {catchList.map(_catch => {
            const { post, _id: catchId, user } = _catch;
            const { _id: postId } = post;
            const { isPremium, nickname, avatar } = user;
            const imageH = get(_catch, k => k.post.images.edges[0].node.height, 0);
            const imageW = get(_catch, k => k.post.images.edges[0].node.width, 0);
            const imageUrl = get(_catch, k => k.post.images.edges[0].node.url, '');

            const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
              getImgProxyLoader(src, width, imageW, imageH, { quality });
            const photoCount = get(_catch, k => k.post.images.totalCount, 1);
            const avatarUrl = avatar ? avatar.url : '';

            return (
              <CatchCard
                key={postId}
                to={getPostUrl(postId)}
                postId={postId}
                userNickname={userNickname}
                targetId={catchId}
                source="OtherCatchesFromThisUser"
              >
                <div className="relative col-start-1 row-start-1 h-full">
                  <NextImage
                    alt={`Catch image from ${nickname}`}
                    className="size-full object-cover object-center"
                    src={imageUrl}
                    height={imageH}
                    width={imageW}
                    loader={imgProxyLoader}
                    sizes="(min-width: 700px) 25vw, 40vw"
                  />
                </div>
                {photoCount > 1 && (
                  <div className="col-start-1 row-start-1 mt-3 ml-3">
                    <MultiPhotoIcon width={32} height={32} ariaLabel={`${photoCount} photos`} />
                  </div>
                )}

                <div className="relative col-start-1 row-start-1 flex h-full w-full flex-col rounded-md md:rounded-lg">
                  <div className="from-trench/85 to-trench/0 mt-auto flex flex-col gap-y-1 rounded-b-md bg-linear-to-t p-3 pt-6 md:rounded-b-lg">
                    <Text
                      as="div"
                      variant="sm"
                      className="flex flex-row items-center gap-x-1.5 font-semibold break-all text-white"
                    >
                      {avatarUrl && (
                        <Avatar
                          alt={nickname}
                          src={avatarUrl}
                          size={AvatarSize.CATCH_CARD}
                          isPro={isPremium || false}
                        />
                      )}
                      {nickname}
                    </Text>
                  </div>
                </div>
              </CatchCard>
            );
          })}
        </div>
      </Grid>
    </div>
  );
};
